import type { CustomerRef, PlaceRef } from '@/model/DeliveryNote'
import type Customer from '@/model/Customer'
import { nonEmptyPlaceRefs } from '@/utils/placeRefHelpers'

export function hasCustomerRef(ref?: CustomerRef): boolean {
  if (!ref) {
    return false
  }
  return !!(ref.id?.trim() || ref.name?.trim() || ref.vatNumber?.trim() || ref.address?.trim())
}

export function isCustomerRefEmpty(ref?: CustomerRef): boolean {
  return !hasCustomerRef(ref)
}

export function emptyCustomerRef(): CustomerRef {
  return {}
}

export function customerRefAt(refs: CustomerRef[] | undefined, index: number): CustomerRef {
  const ref = refs?.[index]
  return ref ? { ...ref } : emptyCustomerRef()
}

export function padCustomerRefs(refs: CustomerRef[] | undefined, length: number): CustomerRef[] {
  const padded: CustomerRef[] = []
  for (let i = 0; i < length; i++) {
    padded.push(customerRefAt(refs, i))
  }
  return padded
}

export function trimCustomerRefsToLength(refs: CustomerRef[], length: number): CustomerRef[] {
  return padCustomerRefs(refs, length)
}

export function removeCustomerRefAt(refs: CustomerRef[], index: number): CustomerRef[] {
  const next = [...refs]
  next.splice(index, 1)
  return next
}

export function reorderCustomerRefs(refs: CustomerRef[], from: number, to: number): CustomerRef[] {
  const next = [...refs]
  const moved = next.splice(from, 1)[0]
  next.splice(to, 0, moved ?? emptyCustomerRef())
  return next
}

export function filterNonemptyCustomerRefs(refs: CustomerRef[]): CustomerRef[] {
  return refs.filter(hasCustomerRef).map((ref) => ({ ...ref }))
}

export function resolveCustomerRefSummary(
  ref: CustomerRef,
  customers: Customer[]
): string {
  if (!hasCustomerRef(ref)) {
    return ''
  }
  if (ref.id?.trim()) {
    const customer = customers.find((c) => c.id === ref.id)
    return customer?.name?.trim() || customer?.businessName?.trim() || ref.id
  }
  return ref.name?.trim() || ref.vatNumber?.trim() || ''
}

export function cloneCustomerRefs(refs: CustomerRef[]): CustomerRef[] {
  return refs.map((ref) => ({ ...ref }))
}

export function customerRefsAlignedToPlaces(
  places: PlaceRef[],
  customers: CustomerRef[] | undefined
): CustomerRef[] {
  return padCustomerRefs(customers, nonEmptyPlaceRefs(places).length).map((ref) =>
    hasCustomerRef(ref) ? { ...ref } : {}
  )
}

export function formatPairedPlacesCustomersDisplay(
  places?: Array<{ name?: string } | null>,
  customers?: Array<{ name?: string; businessName?: string; vatNumber?: string } | null>
): string[] {
  const lines: string[] = []
  const count = places?.length ?? 0
  const namedPlaceCount = (places ?? []).filter((place) => place?.name?.trim()).length
  const listPlaces = namedPlaceCount > 1
  for (let index = 0; index < count; index++) {
    const placeName = places?.[index]?.name?.trim()
    if (placeName) {
      lines.push(listPlaces ? `- ${placeName}` : placeName)
    }
    const customer = customers?.[index]
    const company =
      customer?.businessName?.trim()
      || customer?.name?.trim()
      || customer?.vatNumber?.trim()
    if (company) {
      lines.push(company)
    }
  }
  return lines
}
